import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { Info } from 'react-feather';
import { LinkContext } from '@trinity/chat-components/components/BrandInsights/context';
import AudioPlayerController from '@trinity/chat-components/components/TextToSpeechAudioPlayer/AudioPlayerController.js';
import { Constants } from '@trinity/chat-components/components/Theme';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import AudioControls from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/AudioControls';
import { TextToSpeechStateContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useModal } from '@trinity/chat-components/hooks/useModal';
import {
  MessagesListRefContext,
  ChatHasAccessContext,
  ChatSessionItemsContext,
  AuthorContext,
  ChatLandingCardsVisibleContext,
} from './context';
import { ChatGuidelinesModal } from './GuidelinesModal';
import { useChatGuidelinesCookie } from './hooks/useChatGuidelinesCookie';
import { useChatPermissions } from './hooks/useChatPermissions';
import { UnsubmittedMessage } from './Input';
import { LandingCards } from './LandingCards';
import { messageLaneWidth } from './Message.styles';
import { InitialSystemMessage } from './Message.System.Initial';
import { MessageList } from './MessageList';
import { NoAccessBanner } from './NoAccessBanner';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';

export const ChatInstance = () => {

  const hasAccess = useContext(ChatHasAccessContext);
  const link = useContext(LinkContext);
  const chatPermissions = useChatPermissions();

  const [_, setMessagesRef] = useContext(MessagesListRefContext);
  const author = useContext(AuthorContext);
  const isFirstSession = author.isFirstSession;
  const { isDismissed: chatGuidelinesDismissed } = useChatGuidelinesCookie();
  const [toggleGuidelinesModal, GuidelinesModal] = useModal(ChatGuidelinesModal, {
    defaultOpen: isFirstSession && !chatGuidelinesDismissed,
  });
  const { isDigitalTwin } = useAvatarPanel();

  const features = useContext(GroupFeaturesContext);
  const chatGuidelines = features.chatGuidelines;
  const showChatGuidelines = !!chatGuidelines;
  const disableChatInputWithLandingCards = features.disableChatInputWithLandingCards;

  const items = useContext(ChatSessionItemsContext);

  const showLandingCards = useContext(ChatLandingCardsVisibleContext);

  const renderMessageList = useCallback(() => {
    if (!link.initialized) return null;

    if (showLandingCards) {
      return <LandingCards />;
    }

    if (!items.length) {
      return <InitialSystemMessage />;
    }

    return <MessageList items={items} />;
  }, [items, link.initialized, showLandingCards]);

  const showAudioPlayer = useShowAudioPlayer();

  const renderAudioPlayer = useCallback(() => {
    if (!showAudioPlayer) return null;

    return (
      <AudioPlayerRow>
        <AudioPlayerBox>
          <AudioPlayerController>
            {({
              duration,
              position,
              onScan,
              onSeek,
              onStop,
              onPreviousTrack,
              onNextTrack,
              onSkipBackward,
              onSkipAhead,
              playing,
              onTogglePlay,
              canSkipBackward,
              canSkipForward,
            }) => (
              <AudioControls
                isPlaying={playing}
                onTogglePlay={onTogglePlay}
                onPreviousTrack={onPreviousTrack}
                onNextTrack={onNextTrack}
                onSkipBackward={onSkipBackward}
                onSkipAhead={onSkipAhead}
                disabledPreviousTrack={!canSkipBackward}
                disabledNextTrack={!canSkipForward}
                onStop={onStop}
                currentTime={position}
                duration={duration}
                onScan={onScan}
                onSeek={onSeek}
              />
            )}
          </AudioPlayerController>
        </AudioPlayerBox>
      </AudioPlayerRow>
    );
  }, [showAudioPlayer]);

  return (
    <BrandingProvider>
      <Root>
        {!hasAccess && <NoAccessBanner />}

        <Messages>
          <MessagesWrap ref={setMessagesRef}>
            {renderMessageList()}
          </MessagesWrap>
        </Messages>

        <Footer>
          {renderAudioPlayer()}
          {chatPermissions.canModify && !(disableChatInputWithLandingCards && showLandingCards) && <UnsubmittedMessage />}
          {showChatGuidelines && (
            <DisclaimerText $isDigitalTwin={isDigitalTwin}>
              {!isDigitalTwin && 'Shared content is meant for internal use only and not meant to be shared externally.'}
              <OpenDisclaimer $isDigitalTwin={isDigitalTwin} onClick={toggleGuidelinesModal}>
                View Usage Guidelines <Info size={16} />
              </OpenDisclaimer>
            </DisclaimerText>
          )}
        </Footer>

        {showChatGuidelines && <GuidelinesModal guidelines={chatGuidelines} />}
      </Root>
    </BrandingProvider>
  );
};

function useShowAudioPlayer() {
  const features = useContext(GroupFeaturesContext);
  const audioPlayerState = useContext(TextToSpeechStateContext);
  const audioPlayerEnabled = features.chatReadAloud && features?.chatVoiceSource !== 'browser';

  return useMemo(() => audioPlayerEnabled && (!!audioPlayerState.queryId || !!audioPlayerState.kolSearchId), [audioPlayerEnabled, audioPlayerState]);
}

const MaxContainerWidth = 1100;

const DisclaimerText = styled.div<{ $isDigitalTwin: boolean }>(({ $isDigitalTwin }) => ({
  width: messageLaneWidth,
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  textAlign: $isDigitalTwin ? 'center' : undefined,
}));

const OpenDisclaimer = styled.div<{ $isDigitalTwin: boolean }>(({ theme, $isDigitalTwin }) => ({
  display: 'inline-flex',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: 10,
  marginTop: $isDigitalTwin ? 4 : undefined,
  alignItems: 'center',
  gap: 4,
}));

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100% - ${Constants.Modal.HeaderHeight}px)`,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
});

const Messages = styled.div({
  boxSizing: 'border-box',
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
});

const MessagesWrap = styled.div({
  height: '100%',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
  paddingTop: 15,
  paddingRight: 10,
  paddingLeft: 10,
  boxSizing: 'border-box',
});

const Footer = styled.div(({
  display: 'flex',
  flexDirection: 'column',
  padding: `20px 10px ${15}px 10px`,
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
}));

const AudioPlayerRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'absolute',
  right: 9,
  bottom: `calc(100% - 8px)`,
});

const AudioPlayerBox = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  padding: 10,
  width: 'auto',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

export default ChatInstance;
